import { useQuery } from '@tanstack/react-query';
import { getSession } from '@/api/generated/auth/auth';
import type { User } from '@/api/generated/model';
import { useSessionStore } from '@/lib/session';

export const currentUserKeys = {
  all: ['currentUser'] as const,
  detail: () => [...currentUserKeys.all, 'detail'] as const,
};

/**
 * The signed-in admin, fetched from the server.
 *
 * A query rather than a stored object, because the profile *is* server state
 * and a copy in zustand would start going stale immediately.
 *
 * Unlike the platform app this reads better-auth's `GET /v1/auth/get-session`
 * rather than a `/users/me` route — the admin API has no users module, since
 * admins are platform operators rather than members of anything.
 *
 * Only runs when a token exists, so signed-out screens never fire a doomed
 * request. Never retried: this call is also how a bad token is discovered, and
 * the 401 handler in `@/api/client` has already cleared the session and
 * redirected by the time it would retry.
 */
export function useCurrentUserQuery() {
  const token = useSessionStore((state) => state.token);

  return useQuery({
    queryKey: currentUserKeys.detail(),
    queryFn: () => getSession(),
    enabled: token !== null,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The admin, or null while loading / signed out.
 *
 * `get-session` returns null rather than a 401 for an unrecognised token, so
 * the null case is a real response, not just a loading state.
 */
export function useCurrentUser(): User | null {
  return useCurrentUserQuery().data?.user ?? null;
}
